import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, message, Popconfirm, Switch, Divider, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, ShopOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

const FIELD_LABELS = {
  supplier_id: '主体',
  filter_use: '筛选使用',
  platform: '平台',
  handler: '受理员工',
  team: '小组',
  period: '期数',
  group_name: '群名',
  order_time: '下单时间',
  contact_person: '对接人',
  growth_method: '涨粉方式',
  account_name: '视频号名称/抖音号',
  text3: '文本3',
  link_type: '链接属性',
  exclusive_link: '专属链接/涨粉链接',
  account_duplicate: '账号重复',
  method_duplicate: '方式+账号重复',
  initial_fans: '初始粉丝数',
  final_fans: '涨粉后粉丝数',
  effective_fans: '最终有效粉',
  growth_count: '涨粉量',
  pre_growth_text: '涨粉前账号截图文字',
  post_growth_text: '涨粉完结截图文字',
  status_id: '完结状态',
  complete_date: '完成日期',
  remark: '备注',
  text1: '文本1',
  text2: '文本2',
  reference_price: '参考价格',
  actual_price: '实际价格',
  modified_by_name: '最新修改人'
};

function PermissionTemplate() {
  const { canEdit } = useMenuPermission();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();
  const [templateType, setTemplateType] = useState('user'); // 'user' | 'supplier'

  // 菜单权限
  const [menuPermissions, setMenuPermissions] = useState([]);
  // 字段权限
  const [fieldPermissions, setFieldPermissions] = useState([]);
  // 关联用户弹窗
  const [applyVisible, setApplyVisible] = useState(false);
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [applyUsers, setApplyUsers] = useState([]);
  const [applySuppliers, setApplySuppliers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [applyLoading, setApplyLoading] = useState(false);

  // 数据权限
  const [templatePlatforms, setTemplatePlatforms] = useState([]);
  const [templateTeams, setTemplateTeams] = useState([]);
  const [templateStatuses, setTemplateStatuses] = useState([]);
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);

  // 账单权限
  const [billPermissions, setBillPermissions] = useState([
    { field_name: 'bill_team', label: '小组' },
    { field_name: 'bill_settlement_date', label: '结算日期' },
    { field_name: 'bill_amount', label: '金额' },
    { field_name: 'bill_is_paid', label: '是否打款' },
    { field_name: 'bill_is_invoiced', label: '是否开票' },
    { field_name: 'bill_is_collected', label: '是否回单' },
    { field_name: 'bill_supplier_id', label: '主体' },
    { field_name: 'bill_payee_supplier', label: '打款主体' },
    { field_name: 'bill_pay_supplier', label: '收款主体' },
    { field_name: 'bill_invoice_file', label: '发票上传' },
    { field_name: 'bill_remark', label: '备注' }
  ].map(f => ({ ...f, is_visible: 1, is_editable: 1 })));
  const [templateBillFields, setTemplateBillFields] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/permission-templates', { params: { type: templateType } });
      if (res.code === 200) setData(res.data);
    } catch (err) {}
    setLoading(false);
  }, [templateType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({ allow_add: 1, allow_delete: 1, allow_import: 1, allow_quick_add: 1, allow_batch_assign: 1, allow_batch_edit: 1 });
    loadMenuPermissions([]);
    loadFieldPermissions([]);
    setTemplatePlatforms([]);
    setTemplateTeams([]);
    setTemplateStatuses([]);
    setTemplateBillFields([]);
    loadPlatformsAndTeams();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingTemplate(record);
    try {
      const res = await api.get(`/permission-templates/${record.id}`);
      if (res.code === 200) {
        const tpl = res.data;
        form.setFieldsValue({
          name: tpl.name,
          description: tpl.description,
          allow_add: tpl.allow_add,
          allow_delete: tpl.allow_delete,
          allow_import: tpl.allow_import,
          allow_quick_add: tpl.allow_quick_add,
          allow_batch_assign: tpl.allow_batch_assign,
          allow_batch_edit: tpl.allow_batch_edit
        });
        loadMenuPermissions(tpl.menus || []);
        loadFieldPermissions(tpl.fields || []);
        setTemplatePlatforms(tpl.platforms || []);
        setTemplateTeams(tpl.teams || []);
        setTemplateStatuses(tpl.statuses || []);
        const normBill = [];
        const seenBill = new Set();
        for (const bf of (tpl.bill_fields || [])) {
          if (!bf || !bf.field_name || seenBill.has(bf.field_name)) continue;
          seenBill.add(bf.field_name);
          normBill.push(bf);
        }
        setTemplateBillFields(normBill);
      }
      loadPlatformsAndTeams();
    } catch (err) {}
    setModalVisible(true);
  };

  const loadMenuPermissions = (menus) => {
    if (menus.length > 0) {
      setMenuPermissions(menus.map(m => ({
        key: m.key,
        label: m.label,
        is_visible: m.is_visible,
        is_editable: m.is_editable
      })));
    } else {
      api.get('/permission-templates/meta/menus').then(res => {
        if (res.code === 200) {
          setMenuPermissions(res.data.map(m => ({
            key: m.key,
            label: m.label,
            is_visible: 1,
            is_editable: 1
          })));
        }
      }).catch(() => {});
    }
  };

  const loadFieldPermissions = (fields) => {
    if (fields.length > 0) {
      setFieldPermissions(fields.map(f => ({
        field_name: f.field_name,
        is_visible: f.is_visible,
        is_editable: f.is_editable
      })));
    } else {
      // 主体模板加载不含 supplier_id 的字段列表
      const url = templateType === 'supplier'
        ? '/permission-templates/meta/fields?type=supplier'
        : '/permission-templates/meta/fields';
      api.get(url).then(res => {
        if (res.code === 200) {
          setFieldPermissions(res.data.map(f => ({
            field_name: f,
            is_visible: 1,
            is_editable: 1
          })));
        }
      }).catch(() => {});
    }
  };

  const loadPlatformsAndTeams = async () => {
    try {
      const [platRes, teamRes, statusRes] = await Promise.all([
        api.get('/platforms'),
        api.get('/teams'),
        api.get('/statuses')
      ]);
      if (platRes.code === 200) setAllPlatforms(platRes.data || []);
      if (teamRes.code === 200) setAllTeams(teamRes.data || []);
      if (statusRes.code === 200) setAllStatuses(statusRes.data || []);
    } catch (err) {}
  };

  const handleMenuToggle = (menuKey, field, checked) => {
    setMenuPermissions(prev =>
      prev.map(m => {
        if (m.key !== menuKey) return m;
        const updated = { ...m, [field]: checked ? 1 : 0 };
        if (field === 'is_visible' && !checked) updated.is_editable = 0;
        return updated;
      })
    );
  };

  const handleFieldToggle = (fieldName, key, checked) => {
    setFieldPermissions(prev =>
      prev.map(f => {
        if (f.field_name !== fieldName) return f;
        const updated = { ...f, [key]: checked ? 1 : 0 };
        if (key === 'is_visible' && !checked) updated.is_editable = 0;
        return updated;
      })
    );
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      ['allow_add', 'allow_delete', 'allow_import', 'allow_quick_add', 'allow_batch_assign', 'allow_batch_edit'].forEach(k => {
        if (values[k] !== undefined) values[k] = Number(values[k]);
      });
      const payload = {
        ...values,
        type: templateType,
        menus: menuPermissions.map(m => ({
          key: m.key,
          is_visible: Number(m.is_visible),
          is_editable: Number(m.is_editable)
        })),
        fields: fieldPermissions.map(f => ({
          field_name: f.field_name,
          is_visible: Number(f.is_visible),
          is_editable: Number(f.is_editable)
        })),
        platforms: templatePlatforms || [],
        teams: templateTeams || [],
        statuses: templateStatuses || [],
        bill_fields: (() => {
          const map = new Map();
          (templateBillFields || []).forEach(f => {
            if (f && f.field_name) map.set(f.field_name, {
              field_name: f.field_name,
              is_visible: Number(f.is_visible ?? 1),
              is_editable: Number(f.is_editable ?? 1)
            });
          });
          return Array.from(map.values());
        })()
      };

      let res;
      if (editingTemplate) {
        res = await api.put(`/permission-templates/${editingTemplate.id}`, payload);
      } else {
        res = await api.post('/permission-templates', payload);
      }

      if (res.code === 200) {
        message.success(editingTemplate ? res.message || '更新成功' : '创建成功');
        setModalVisible(false);
        loadData();
        window.dispatchEvent(new Event('userUpdated'));
      }
    } catch (err) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/permission-templates/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        loadData();
      }
    } catch (err) {}
  };

  // 关联用户/主体
  const handleApply = async (record) => {
    setApplyTemplate(record);
    setApplyUsers([]);
    setApplySuppliers([]);
    setApplyLoading(true);
    try {
      if (templateType === 'user') {
        const res = await api.get('/users', { params: { pageSize: 1000 } });
        if (res.code === 200) {
          setAllUsers(res.data.list.filter(u => u.username !== 'admin'));
        }
      } else {
        const res = await api.get('/suppliers', { params: { pageSize: 1000 } });
        if (res.code === 200) {
          setAllSuppliers(res.data.list || []);
        }
      }
    } catch (err) {}
    setApplyLoading(false);
    setApplyVisible(true);
  };

  const handleApplySubmit = async () => {
    const body = {};
    if (templateType === 'user') {
      if (applyUsers.length === 0) { message.warning('请选择用户'); return; }
      body.user_ids = applyUsers;
    } else {
      if (applySuppliers.length === 0) { message.warning('请选择主体'); return; }
      body.supplier_ids = applySuppliers;
    }
    try {
      const res = await api.post(`/permission-templates/${applyTemplate.id}/apply`, body);
      if (res.code === 200) {
        message.success(res.message);
        setApplyVisible(false);
      }
    } catch (err) {}
  };

  const handleMenuSelectAll = (field, value) => {
    setMenuPermissions(prev => prev.map(m => {
      const updated = { ...m, [field]: value };
      if (field === 'is_visible' && !value) updated.is_editable = 0;
      return updated;
    }));
  };

  const handleBillToggle = (fieldName, key, checked) => {
    setTemplateBillFields(prev => {
      const exists = prev.find(f => f.field_name === fieldName);
      if (exists) return prev.map(f => f.field_name === fieldName ? { ...f, [key]: checked ? 1 : 0 } : f);
      return [...prev, { field_name: fieldName, is_visible: 1, is_editable: 1, [key]: checked ? 1 : 0 }];
    });
  };

  const handleBillSelectAll = (key, value) => {
    setTemplateBillFields(prev => {
      const updated = billPermissions.map(b => {
        const existing = prev.find(f => f.field_name === b.field_name);
        if (existing) return { ...existing, [key]: value };
        return { field_name: b.field_name, is_visible: 1, is_editable: 1, [key]: value };
      });
      return updated;
    });
  };

  const billPermColumns = [
    { title: '字段名', dataIndex: 'field_name', width: 180 },
    { title: '中文名', dataIndex: 'label' },
    { title: '可见', dataIndex: 'is_visible', width: 70, align: 'center',
      render: (v, r) => <Switch checked={v === 1} size="small" onChange={c => handleBillToggle(r.field_name, 'is_visible', c)} /> },
    { title: '可编辑', dataIndex: 'is_editable', width: 70, align: 'center',
      render: (v, r) => <Switch checked={v === 1} size="small" onChange={c => handleBillToggle(r.field_name, 'is_editable', c)} /> }
  ];

  const handleFieldSelectAll = (field, value) => {
    setFieldPermissions(prev => prev.map(f => {
      const updated = { ...f, [field]: value };
      if (field === 'is_visible' && !value) updated.is_editable = 0;
      return updated;
    }));
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '模板名称', dataIndex: 'name', width: 200 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '新增', dataIndex: 'allow_add', width: 60, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '删除', dataIndex: 'allow_delete', width: 60, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '导入', dataIndex: 'allow_import', width: 60, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '快速导入', dataIndex: 'allow_quick_add', width: 80, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '批量分配', dataIndex: 'allow_batch_assign', width: 80, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '批量修改', dataIndex: 'allow_batch_edit', width: 80, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '创建时间', dataIndex: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: '操作', key: 'action', width: 280,
      render: (_, record) => (
        <Space>
          {canEdit && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>}
          {canEdit && (
            <Button type="link" size="small" icon={templateType === 'supplier' ? <ShopOutlined /> : <UserOutlined />} onClick={() => handleApply(record)}>
              {templateType === 'supplier' ? '关联主体' : '关联用户'}
            </Button>
          )}
          {canEdit && (
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const menuPermColumns = [
    { title: '菜单名称', dataIndex: 'label' },
    { title: '菜单标识', dataIndex: 'key', render: v => <Tag>{v}</Tag> },
    {
      title: '是否可见', dataIndex: 'is_visible', width: 100,
      render: (v, record) => (
        <Switch checked={v === 1} size="small" onChange={(checked) => handleMenuToggle(record.key, 'is_visible', checked)} />
      )
    },
    {
      title: '是否可编辑', dataIndex: 'is_editable', width: 100,
      render: (v, record) => (
        <Switch checked={v === 1} size="small" disabled={record.is_visible === 0} onChange={(checked) => handleMenuToggle(record.key, 'is_editable', checked)} />
      )
    }
  ];

  const fieldPermColumns = [
    {
      title: '字段名称', dataIndex: 'field_name',
      render: v => FIELD_LABELS[v] || v
    },
    { title: '字段标识', dataIndex: 'field_name', render: v => <Tag>{v}</Tag> },
    {
      title: '是否可见', dataIndex: 'is_visible', width: 100,
      render: (v, record) => (
        <Switch checked={v === 1} size="small" onChange={(checked) => handleFieldToggle(record.field_name, 'is_visible', checked)} />
      )
    },
    {
      title: '是否可编辑', dataIndex: 'is_editable', width: 100,
      render: (v, record) => (
        <Switch checked={v === 1} size="small" disabled={record.is_visible === 0} onChange={(checked) => handleFieldToggle(record.field_name, 'is_editable', checked)} />
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>权限关联</h3>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增模板</Button>}
      </div>

      <Tabs
        activeKey={templateType}
        onChange={key => { setTemplateType(key); setData([]); }}
        items={[
          { key: 'user', label: '用户模板' },
          { key: 'supplier', label: '主体模板' }
        ]}
      />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={false}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={`${editingTemplate ? '编辑' : '新增'}${templateType === 'supplier' ? '主体' : '用户'}权限模板`}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={900}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请输入模板名称' }]} style={{ flex: 1 }}>
              <Input placeholder="如：运营专员、数据分析师" />
            </Form.Item>
            <Form.Item name="description" label="描述" style={{ flex: 1 }}>
              <Input placeholder="可选描述" />
            </Form.Item>
          </div>

          <Divider orientation="left">操作权限</Divider>
          <Space style={{ marginBottom: 16 }} wrap>
            <Form.Item name="allow_add" label="新增订单" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
            <Form.Item name="allow_delete" label="删除订单" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
            <Form.Item name="allow_import" label="批量导入" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
            <Form.Item name="allow_quick_add" label="快速导入" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
            <Form.Item name="allow_batch_assign" label="批量分配" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
            <Form.Item name="allow_batch_edit" label="批量修改" style={{ marginBottom: 0 }}>
              <Select style={{ width: 100 }} options={[{ label: '允许', value: 1 }, { label: '禁止', value: 0 }]} />
            </Form.Item>
          </Space>

          <Tabs
            items={[
              {
                key: 'menus',
                label: '菜单权限',
                children: (
                  <>
                    <Space style={{ marginBottom: 12 }}>
                      <Button size="small" onClick={() => handleMenuSelectAll('is_visible', 1)}>全部可见</Button>
                      <Button size="small" onClick={() => handleMenuSelectAll('is_visible', 0)}>全部隐藏</Button>
                      <Button size="small" onClick={() => handleMenuSelectAll('is_editable', 1)}>全部可编辑</Button>
                      <Button size="small" onClick={() => handleMenuSelectAll('is_editable', 0)}>全部只读</Button>
                    </Space>
                    <Table
                      rowKey="key"
                      columns={menuPermColumns}
                      dataSource={menuPermissions}
                      pagination={false}
                      size="small"
                    />
                  </>
                )
              },
              {
                key: 'fields',
                label: '字段权限',
                children: (
                  <>
                    <Space style={{ marginBottom: 12 }}>
                      <Button size="small" onClick={() => handleFieldSelectAll('is_visible', 1)}>全部可见</Button>
                      <Button size="small" onClick={() => handleFieldSelectAll('is_visible', 0)}>全部隐藏</Button>
                      <Button size="small" onClick={() => handleFieldSelectAll('is_editable', 1)}>全部可编辑</Button>
                      <Button size="small" onClick={() => handleFieldSelectAll('is_editable', 0)}>全部只读</Button>
                    </Space>
                    <Table
                      rowKey="field_name"
                      columns={fieldPermColumns}
                      dataSource={fieldPermissions}
                      pagination={false}
                      size="small"
                    />
                  </>
                )
              },
              {
                key: 'bill',
                label: '账单权限',
                children: (
                  <>
                    <Space style={{ marginBottom: 12 }}>
                      <Button size="small" onClick={() => handleBillSelectAll('is_visible', 1)}>全部可见</Button>
                      <Button size="small" onClick={() => handleBillSelectAll('is_visible', 0)}>全部隐藏</Button>
                      <Button size="small" onClick={() => handleBillSelectAll('is_editable', 1)}>全部可编辑</Button>
                      <Button size="small" onClick={() => handleBillSelectAll('is_editable', 0)}>全部只读</Button>
                    </Space>
                    <Table
                      rowKey="field_name"
                      columns={billPermColumns}
                      dataSource={billPermissions.map(b => {
                        const saved = (templateBillFields || []).find(f => f.field_name === b.field_name);
                        return saved ? { ...b, is_visible: saved.is_visible, is_editable: saved.is_editable } : b;
                      })}
                      pagination={false}
                      size="small"
                    />
                  </>
                )
              },
              {
                key: 'data',
                label: '数据权限',
                children: (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的平台</div>
                      <p style={{ color: '#999', marginBottom: 8 }}>不选择任何平台 = 查看所有平台的订单</p>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="选择允许的平台（留空=全部）"
                        value={templatePlatforms}
                        onChange={setTemplatePlatforms}
                      >
                        <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                        {allPlatforms.map(p => (
                          <Select.Option key={p.name} value={p.name}>{p.name}</Select.Option>
                        ))}
                      </Select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的小组</div>
                      <p style={{ color: '#999', marginBottom: 8 }}>不选择任何小组 = 查看所有小组的订单</p>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="选择允许的小组（留空=全部）"
                        value={templateTeams}
                        onChange={setTemplateTeams}
                      >
                        <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                        {allTeams.map(t => (
                          <Select.Option key={t.name} value={t.name}>{t.name}</Select.Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: 8 }}>限制修改的状态</div>
                      <p style={{ color: '#999', marginBottom: 8 }}>选中的状态将禁止该用户/主体修改。状态管理中锁定的状态对所有人限制，优先级最高。</p>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="选择要限制的状态（留空=不限制）"
                        value={templateStatuses}
                        onChange={setTemplateStatuses}
                      >
                        {allStatuses.filter(s => !s.is_locked).map(s => (
                          <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                        ))}
                      </Select>
                    </div>
                  </>
                )
              }
            ]}
          />
        </Form>
      </Modal>

      {/* 关联用户弹窗 */}
      <Modal
        title={`${templateType === 'supplier' ? '关联主体' : '关联用户'} - ${applyTemplate?.name}`}
        open={applyVisible}
        onOk={handleApplySubmit}
        onCancel={() => setApplyVisible(false)}
        confirmLoading={applyLoading}
      >
        {templateType === 'user' ? (
          <>
            <p style={{ marginBottom: 12, color: '#666' }}>选择要应用此权限模板的用户（不包括超管）：</p>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="请选择用户"
              value={applyUsers}
              onChange={setApplyUsers}
              options={allUsers.map(u => ({ label: `${u.name} (${u.username})`, value: u.id }))}
            />
          </>
        ) : (
          <>
            <p style={{ marginBottom: 12, color: '#666' }}>选择要应用此权限模板的主体：</p>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="请选择主体"
              value={applySuppliers}
              onChange={setApplySuppliers}
              options={allSuppliers.map(s => ({ label: s.name, value: s.id }))}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

export default PermissionTemplate;
